import { useEffect, useRef, useState, type CSSProperties } from "react";

import { DAY_MS, parseDay } from "../domain/date";

export type Point = [string, number];

const H = 160;
/** Gridline values are labelled in this gutter, at the left edge. */
const PAD_L = 30;
/** Room at the right for the "trend" and target labels past the last point. */
const PAD_R = 40;
const PAD_T = 16;
/** The plot ends at 128; the date axis sits at 152. */
const PAD_B = 32;
/** A .8rem label's height, for keeping one clear of a line. */
const LABEL_PX = 13;

function shortDate(ds: string): string {
  return parseDay(ds).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function fmtV(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

/** Two or three round values inside [lo, hi] for the gridlines. */
function niceTicks(lo: number, hi: number): number[] {
  for (const step of [0.5, 1, 2, 2.5, 5, 10, 20, 25, 50, 100, 250, 500]) {
    const ticks: number[] = [];
    for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) ticks.push(+v.toFixed(2));
    if (ticks.length <= 3) return ticks;
  }
  return [];
}

/**
 * Weight and waist over time (SNIPPETS §18): faint dots for the readings, the smoothed line
 * carrying the eye, a dashed projection, a success-coloured target rule, and direct labels —
 * "trend" at the line's end, the target value on its rule. No legend box; the labels are the key.
 *
 * x is placed by date, so a three-week gap between weigh-ins looks like one. Drawn at the
 * element's real pixel width rather than stretched with `preserveAspectRatio="none"`, which
 * squashed every label and turned dots into ovals.
 *
 * `bands` shades date ranges (the pre-period days) in the cycle tint and `tagged` draws the
 * readings those days explain in cycle ink — tagged, never hidden (ADR-033).
 *
 * When to use: Progress's hero and /progress/body. Without `trend` the points themselves are the
 * line. Fewer than two points: no chart, one sentence.
 */
export function TrendChart({
  pts,
  color = "var(--brand)",
  target,
  unit,
  trend,
  projection,
  band,
  bands,
  tagged,
  readings = "weigh-ins",
  ariaLabel,
}: {
  pts: Point[];
  /** the line, projection and today dot; brand by default */
  color?: string;
  /** a target value; 0 or undefined draws no rule */
  target?: number;
  /** appended to the target's label: "72 kg" */
  unit?: string;
  /** what the faint dots are, for the accessible name */
  readings?: string;
  /** smoothed series (EWMA), drawn bold */
  trend?: Point[];
  /** where the trend goes if the current rate holds, drawn dashed */
  projection?: Point[];
  /** a shaded value range, e.g. a healthy range */
  band?: [number, number];
  /** date ranges shaded in the cycle tint behind the series — the pre-period days */
  bands?: { from: string; to: string }[];
  /** reading dates drawn in the cycle ink rather than rest: the ones those days explain */
  tagged?: ReadonlySet<string>;
  ariaLabel?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState(0);
  const drawable = pts.length >= 2;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => setMeasured(el.clientWidth);
    set();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [drawable]);

  if (!drawable)
    return (
      <p className="k-chart__empty">
        {pts.length ? "One entry so far. The line appears from the second." : "No entries yet."}
      </p>
    );

  // Before the first measurement there is no width to draw at; 358 is the kit's column.
  const W = measured || 358;
  const smooth = trend && trend.length ? trend : null;
  const proj = smooth && projection && projection.length >= 2 ? projection : null;
  const all = [...pts, ...(smooth || []), ...(proj || [])];
  const ts = all.map((p) => parseDay(p[0]).getTime());
  const t0 = Math.min(...ts);
  const t1 = Math.max(Math.max(...ts), t0 + DAY_MS);

  let vals = all.map((p) => p[1]);
  if (target) vals = vals.concat([target]);
  if (band) vals = vals.concat(band);
  let lo = Math.min(...vals);
  let hi = Math.max(...vals);
  if (hi - lo < 2) {
    hi += 1;
    lo -= 1;
  }

  const plotR = W - PAD_R;
  const x = (ds: string) => PAD_L + ((parseDay(ds).getTime() - t0) * (plotR - PAD_L)) / (t1 - t0);
  const y = (v: number) => H - PAD_B - ((v - lo) * (H - PAD_T - PAD_B)) / (hi - lo);
  // A day is a column centred on its point; a band covers whole days, clipped to the plot.
  const halfDay = ((plotR - PAD_L) * DAY_MS) / (t1 - t0) / 2;
  const shaded = (bands ?? [])
    .map((b) => ({ x0: Math.max(PAD_L, x(b.from) - halfDay), x1: Math.min(plotR, x(b.to) + halfDay), key: b.from }))
    .filter((b) => b.x1 > b.x0);
  const poly = (ps: Point[]) => ps.map((p) => `${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(" ");

  const line = smooth ?? pts;
  const last = line[line.length - 1];
  const lx = x(last[0]);
  const ly = y(last[1]);
  // "trend" sits up and to the right of today's dot, or to its left when that runs off the edge.
  const trendRight = lx + 8 + 40 <= W;

  const ty = target ? y(target) : 0;
  const tLabelY = target ? (ty - 6 - LABEL_PX >= 0 ? ty - 6 : ty + LABEL_PX + 4) : 0;
  const ticks = niceTicks(lo, hi).filter((v) => !target || Math.abs(y(v) - ty) > LABEL_PX);

  // Month names when the span is long enough to hold three; otherwise the first and last dates.
  const firstDs = all.reduce((a, p) => (p[0] < a ? p[0] : a), all[0][0]);
  const lastDs = all.reduce((a, p) => (p[0] > a ? p[0] : a), all[0][0]);
  const months: { x: number; label: string; anchor: "start" | "middle" }[] = [];
  const start = new Date(t0);
  const m = new Date(start.getFullYear(), start.getMonth(), 1);
  if (start.getDate() > 3) m.setMonth(m.getMonth() + 1);
  for (; m.getTime() <= t1; m.setMonth(m.getMonth() + 1)) {
    const mx = PAD_L + ((m.getTime() - t0) * (plotR - PAD_L)) / (t1 - t0);
    const edge = mx - PAD_L < 12;
    if (mx > W - 14) break;
    months.push({
      x: edge ? PAD_L : mx,
      label: m.toLocaleDateString(undefined, { month: "short" }),
      anchor: edge ? "start" : "middle",
    });
  }
  const byMonth = months.length >= 3;

  const name =
    ariaLabel ??
    `Trend from ${fmtV(+(line[0][1]).toFixed(1))} on ${shortDate(line[0][0])} to ${fmtV(
      +last[1].toFixed(1)
    )} on ${shortDate(last[0])}, from ${pts.length} ${readings}` +
      (target ? `; target ${fmtV(target)}${unit ? " " + unit : ""}` : "") +
      (shaded.length ? "; pre-period days shaded" : "");

  return (
    <div ref={ref} className="k-chart chartbox" style={{ "--c-line": color } as CSSProperties}>
      <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label={name}>
        {shaded.map((b) => (
          <rect key={b.key} className="g-cyc" x={b.x0} width={b.x1 - b.x0} y={PAD_T} height={H - PAD_T - PAD_B} />
        ))}
        {band ? (
          <rect
            className="g-band"
            x={PAD_L}
            width={plotR - PAD_L}
            y={y(Math.max(...band))}
            height={Math.abs(y(band[0]) - y(band[1]))}
          />
        ) : null}
        {ticks.map((v) => (
          <g key={v}>
            <line className="g-grid" x1={PAD_L} y1={y(v)} x2={W} y2={y(v)} />
            <text x={0} y={y(v) + 4.5}>
              {fmtV(v)}
            </text>
          </g>
        ))}
        {target ? (
          <>
            <line className="g-target" x1={PAD_L} y1={ty} x2={W} y2={ty} />
            <text className="t-success" x={W} y={tLabelY} textAnchor="end">
              {fmtV(target)}
              {unit ? ` ${unit}` : ""}
            </text>
          </>
        ) : null}
        {smooth
          ? pts.map((p, i) => (
              <circle key={i} className={tagged?.has(p[0]) ? "g-dot is-cyc" : "g-dot"} cx={x(p[0])} cy={y(p[1])} r="2.5" />
            ))
          : null}
        <polyline className="g-trend" points={poly(line)} />
        {proj ? <polyline className="g-proj" points={poly(proj)} /> : null}
        <circle className="g-now" cx={lx} cy={ly} r="4" />
        {smooth ? (
          <text className="t-line" x={trendRight ? lx + 8 : lx - 8} y={ly - 8} textAnchor={trendRight ? "start" : "end"}>
            trend
          </text>
        ) : null}
        {byMonth ? (
          months.map((mo) => (
            <text key={mo.x} x={mo.x} y={H - 8} textAnchor={mo.anchor}>
              {mo.label}
            </text>
          ))
        ) : (
          <>
            <text x={PAD_L} y={H - 8}>
              {shortDate(firstDs)}
            </text>
            <text x={plotR} y={H - 8} textAnchor="end">
              {shortDate(lastDs)}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}

/** The old name, kept so Home and Progress compile until they are retired. */
export const Chart = TrendChart;
